/**
 * Simple event-based coordination between search and chat components
 */

type CoordinationEvent = 'search-opened' | 'search-closed';
type EventCallback = () => void;

class SearchChatCoordination {
  private listeners = new Map<CoordinationEvent, Set<EventCallback>>();
  private wasChatOpenBeforeSearch = false;

  subscribe(event: CoordinationEvent, callback: EventCallback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => this.listeners.get(event)?.delete(callback);
  }

  private emit(event: CoordinationEvent) {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error(`Error in ${event} callback:`, error);
      }
    });
  }

  // Public API
  searchOpened() {
    this.emit('search-opened');
  }

  searchClosed() {
    this.emit('search-closed');
  }

  setChatWasOpen(wasOpen: boolean) {
    this.wasChatOpenBeforeSearch = wasOpen;
  }

  getChatWasOpen(): boolean {
    return this.wasChatOpenBeforeSearch;
  }
}

// Global instance
const coordination = new SearchChatCoordination();

// Hook for React components
export function useSearchChatCoordination() {
  return {
    onSearchOpened: () => coordination.searchOpened(),
    onSearchClosed: () => coordination.searchClosed(),
    subscribe: coordination.subscribe.bind(coordination),
    setChatWasOpenBeforeSearch: coordination.setChatWasOpen.bind(coordination),
    getChatWasOpenBeforeSearch: coordination.getChatWasOpen.bind(coordination),
    // Legacy compatibility - no-op function
    onChatStateChanged: () => {},
  };
}